import type { ProjectProduct } from '@xver/apis/v1/projects/model';
import { useActiveProject } from '@xver/hooks/useActiveProject';
import { billedToName } from '@xver/utils/projects';
import { useTranslation } from 'react-i18next';

interface DeployTargetNoticeProps {
    product: ProjectProduct;
    /** Id given to the reason text; point the blocked deploy button's `aria-describedby` at it. */
    reasonId: string;
}

/**
 * "Deploying to <project> · Billed to <payer or owner>" for a deploy summary, plus the reason the
 * caller can't deploy when `useActiveProject().can(product, 'manage')` is false.
 */
export default function DeployTargetNotice({ product, reasonId }: DeployTargetNoticeProps) {
    const { t } = useTranslation('projects');
    const { project, isResolved, can } = useActiveProject();

    return (
        <div className="w-full flex flex-col gap-2 text-[13px]">
            {project && (
                <p className="text-fg/78">
                    {t('deploy.target', {
                        project: project.name,
                        payer: billedToName(project) ?? t('deploy.billedToPayer'),
                    })}
                </p>
            )}
            {!can(product, 'manage') && (
                <p
                    id={reasonId}
                    className="p-2 rounded-md border-[0.5px] border-line bg-surface-2 text-fg"
                >
                    {isResolved
                        ? t('deploy.blocked', {
                              product: t(`products.${product}`),
                              project: project?.name ?? '',
                          })
                        : t('deploy.checking')}
                </p>
            )}
        </div>
    );
}
